import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LogoMark from "../images/LogoMark";
import Button from "../ui/Button";
import "./AuthPage.css";
import {login, register} from "../../api/Client";

type Mode = "login" | "signup";

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const EMPTY_FORM: FormState = { firstName: "", lastName: "", email: "", password: ""};

export default function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>(location.pathname === "/signup" ? "signup" : "login");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // "America/New_York"

  const mutation = useMutation({
    mutationFn: () =>
      mode === "login"
        ? login(form.email, form.password)
        : register(form.email, form.password, form.firstName, form.lastName, timezone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      // A fresh signup needs to prove the email before going any further —
      // login (an already-verified account) goes straight in.
      if (mode === "signup") {
        navigate("/verify-email", { replace: true, state: { email: form.email } });
      } else {
        navigate("/learn", { replace: true });
      }
    },
  });

  const switchMode = (next: Mode) => {
    setMode(next);
    setForm(EMPTY_FORM);
    setFormError(null);
    mutation.reset();
    navigate(next === "signup" ? "/signup" : "/login", { replace: true });
  };

  const handleChange = (field: keyof FormState) => (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    mutation.mutate();
  };

  const errorMessage =
    formError ?? (mutation.isError ? (mutation.error as Error).message : null);

  return (
    <div className="auth-page">
      <header className="auth-page__header">
        <div className="auth-page__header-inner">
          <Link to="/" className="auth-page__brand">
            <LogoMark size={26} />
            <span>Eolass</span>
          </Link>
        </div>
      </header>

      <main className="auth-page__main">
        <div className="auth-card">
          <div className="auth-card__tabs" role="tablist" aria-label="Log in or create an account">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={`auth-card__tab ${mode === "login" ? "auth-card__tab--active" : ""}`}
              onClick={() => switchMode("login")}
            >
              Log in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={`auth-card__tab ${mode === "signup" ? "auth-card__tab--active" : ""}`}
              onClick={() => switchMode("signup")}
            >
              Create account
            </button>
          </div>

          <div className="auth-card__body">
            <h1 className="auth-card__title">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              {mode === "signup" && (
                  <div className="auth-form__row">
                    <label className="auth-form__field">
                      <span>First name</span>
                      <input
                          type="text"
                          name="firstName"
                          autoComplete="given-name"
                          required
                          value={form.firstName}
                          onChange={handleChange("firstName")}
                      />
                    </label>
                    <label className="auth-form__field">
                      <span>Last name</span>
                      <input
                          type="text"
                          name="lastName"
                          autoComplete="family-name"
                          required
                          value={form.lastName}
                          onChange={handleChange("lastName")}
                      />
                    </label>
                  </div>
              )}

              <label className="auth-form__field">
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                />
              </label>

              <label className="auth-form__field">
                <span>Password</span>
                <input
                  type="password"
                  name="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  minLength={8}
                  value={form.password}
                  onChange={handleChange("password")}
                />
              </label>

              {/*{mode === "login" && (*/}
              {/*  <a href="/forgot-password" className="auth-form__forgot">*/}
              {/*    Forgot your password?*/}
              {/*  </a>*/}
              {/*)}*/}

              {errorMessage && (
                <p className="auth-form__error" role="alert">
                  {errorMessage}
                </p>
              )}

              {mode === "signup" && (
                <p className="auth-form__terms">
                  By creating an account, you agree to our{" "}
                  <a href="/terms.pdf" target="_blank" rel="noopener noreferrer">Terms</a>
                  {" "}and{" "}
                  <a href="/privacy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="auth-form__submit"
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? "Working on it…"
                  : mode === "login"
                  ? "Log in"
                  : "Create account"}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
